import React, { useContext, useEffect, useState, useMemo } from 'react';
import { AuthContext, API } from '@/App';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Gem, Sparkles, RefreshCw, TrendingUp, TrendingDown, Clock, 
  ShieldCheck, Flame, PiggyBank, ArrowRight, DollarSign, 
  AlertTriangle, CheckCircle2, ChevronRight, Target, Compass, 
  Wallet, Layers, Lock
} from 'lucide-react';
import { formatCurrency } from '@/utils/currencyFormat';

import BACKEND_URL from '@/config/backendUrl';


const AdvancedAnalytics = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [latestInsight, setLatestInsight] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);

  const isPro = user?.subscription_tier === 'pro' && (user?.subscription_status === 'active' || user?.subscription_status === 'trialing');
  const isPremium = user?.subscription_tier === 'premium' && (user?.subscription_status === 'active' || user?.subscription_status === 'trialing');
  const isFree = !isPro && !isPremium;

  useEffect(() => {
    if (token) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transRes, catRes, accRes] = await Promise.all([
        axios.get(`${API}/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setTransactions(transRes.data || []);
      setCategories(catRes.data || []);
      setAccounts(accRes.data || []);

      // Intentar cargar último insight de IA
      try {
        const aiRes = await fetch(`${BACKEND_URL}/api/ai/insights/latest`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const aiData = await aiRes.json();
        if (aiRes.ok && aiData.success && aiData.insight) {
          setLatestInsight(aiData.insight);
        }
      } catch (err) {
        console.warn('AI insight fetch skipped:', err);
      }
    } catch (error) {
      console.error('Error fetching advanced analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAI = async () => {
    try {
      setGeneratingAI(true);
      const response = await fetch(`${BACKEND_URL}/api/ai/insights/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force_regenerate: true })
      });
      const data = await response.json();
      if (response.ok && data.success && data.insight) {
        setLatestInsight(data.insight);
      }
    } catch (error) {
      console.error('Error generating AI analysis:', error);
    } finally {
      setGeneratingAI(false);
    }
  };

  const [isAIExpanded, setIsAIExpanded] = useState(false);

  // 1. Detector de Gastos Hormiga (Transacciones <= $150 MXN)
  const microExpensesData = useMemo(() => {
    const microExpenses = (transactions || []).filter(
      t => t.type === 'expense' && Number(t.amount || 0) > 0 && Number(t.amount || 0) <= 150
    );
    const totalMicro = microExpenses.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const count = microExpenses.length;
    const projectedAnnual = totalMicro * 12;

    const allExpensesTotal = (transactions || [])
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);
    
    const percentageOfTotal = allExpensesTotal > 0 ? ((totalMicro / allExpensesTotal) * 100).toFixed(1) : 0;

    return {
      totalMicro,
      count,
      projectedAnnual,
      percentageOfTotal,
      recent: microExpenses.slice(0, 4)
    };
  }, [transactions]);

  // 2. Cálculo de Runway Financiero (Meses de Libertad)
  const runwayData = useMemo(() => {
    const totalAssets = (accounts || []).reduce((sum, a) => {
      if (a.type === 'credit_card' || a.is_active === false) return sum;
      const amt = a.currency === 'USD' ? (a.current_balance || 0) * 17 : (a.current_balance || 0);
      return sum + amt;
    }, 0);

    const expenseTrans = (transactions || []).filter(t => t.type === 'expense');
    const totalExpenses = expenseTrans.reduce((sum, t) => sum + Number(t.amount || 0), 0);

    // Calcular meses distintos con gastos
    const months = new Set(expenseTrans.map(t => (t.date || '').slice(0, 7)).filter(Boolean));
    const monthCount = Math.max(1, months.size);
    const avgMonthlyExpense = totalExpenses / monthCount;

    const monthsRunway = avgMonthlyExpense > 0 ? (totalAssets / avgMonthlyExpense).toFixed(1) : '∞';

    let status = 'normal';
    let statusLabel = '3 a 6 meses de respaldo';
    let statusColor = 'text-amber-500 bg-amber-50 border-amber-200';
    if (Number(monthsRunway) >= 6 || monthsRunway === '∞') {
      status = 'excellent';
      statusLabel = 'Fondo de Libertad Saludable (> 6 meses)';
      statusColor = 'text-emerald-600 bg-emerald-50 border-emerald-200';
    } else if (Number(monthsRunway) < 2) {
      status = 'danger';
      statusLabel = 'Menos de 2 meses de respaldo';
      statusColor = 'text-rose-600 bg-rose-50 border-rose-200';
    }

    return {
      totalAssets,
      avgMonthlyExpense,
      monthsRunway,
      status,
      statusLabel,
      statusColor
    };
  }, [accounts, transactions]);

  // 3. Tasa de Retención de Capital (% de Ahorro Real)
  const retentionData = useMemo(() => {
    const totalIncome = (transactions || [])
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const totalExpense = (transactions || [])
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);

    const net = totalIncome - totalExpense;
    const retentionRate = totalIncome > 0 ? Math.round((net / totalIncome) * 100) : 0;

    return {
      totalIncome,
      totalExpense,
      net,
      retentionRate: Math.max(-100, Math.min(100, retentionRate))
    };
  }, [transactions]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="text-slate-500 font-medium text-sm">Cargando análisis avanzado...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Gate para usuarios Free
  if (isFree) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6 px-4 py-8">
          <Card className="p-8 bg-gradient-to-br from-indigo-900 via-slate-900 to-purple-950 border-none shadow-2xl rounded-3xl text-white text-center space-y-5">
            <div className="inline-flex p-4 bg-white/10 rounded-2xl relative">
              <Gem className="w-10 h-10 text-purple-400 animate-pulse" />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-md border border-white/20">
                <Lock className="w-3.5 h-3.5" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-500/20 text-purple-300 border border-purple-400/30 mb-2">
                <Lock className="w-3 h-3" /> Exclusivo Plan Pro
              </div>
              <h2 className="text-2xl font-black tracking-tight">Análisis Avanzado e Inteligencia Artificial</h2>
            </div>
            <p className="text-sm text-slate-300 max-w-md mx-auto leading-relaxed">
              Descubre tu tiempo de supervivencia financiera (Runway), fugas de dinero por gastos hormiga, y diagnósticos generados con IA.
            </p>
            <Button
              onClick={() => navigate('/pricing')}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2"
            >
              <Lock className="w-4 h-4" />
              <span>Desbloquear con Plan Pro</span>
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 py-6 max-w-5xl mx-auto pb-14 animate-fadeIn">
        {/* Header Principal */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-widest">
              <Sparkles className="w-4 h-4 animate-pulse" />
              <span>Diagnóstico Inteligente</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Análisis Avanzado Pro
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5">
              Predicciones de IA, libertad financiera y detección de fugas invisibles.
            </p>
          </div>

          <Link
            to="/ai-insights"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-xl font-bold text-xs transition-colors self-start sm:self-auto border border-indigo-100"
          >
            <span>Ver Historial IA</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* 1. SECCIÓN AI INSIGHTS INTEGRADO (Contraída por defecto) */}
        <div className="p-1 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-xl shadow-indigo-500/10">
          <div className="bg-white rounded-[1.4rem] p-5 sm:p-6 space-y-3">
            <div 
              onClick={() => setIsAIExpanded(!isAIExpanded)}
              className="flex items-center justify-between gap-3 cursor-pointer select-none"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20 shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-black text-slate-900 text-base sm:text-lg truncate">Diagnóstico Inteligente por IA</h3>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 shrink-0 border border-indigo-100">
                      {isAIExpanded ? 'Expandido' : 'Contraído'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 font-medium truncate">
                    {latestInsight?.created_at ? `Última actualización: ${new Date(latestInsight.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}` : 'Toca para abrir o contraer el análisis'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-bold text-indigo-600 hidden sm:inline">
                  {isAIExpanded ? 'Contraer' : 'Abrir'}
                </span>
                <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isAIExpanded ? '-rotate-90' : 'rotate-90'}`} />
                </div>
              </div>
            </div>

            {/* Resumen ejecutivo visible cuando está contraído */}
            {!isAIExpanded && (
              <p className="text-xs text-slate-600 line-clamp-2 bg-slate-50 p-3 rounded-xl border border-slate-100 mt-2 font-medium">
                💡 {latestInsight?.summary || "Consulta recomendaciones y predicciones inteligentes generadas a partir de tus hábitos de gasto y ahorro."}
              </p>
            )}

            {/* Contenido completo expandible */}
            {isAIExpanded && (
              <div className="space-y-4 pt-3 border-t border-slate-100 animate-fadeIn">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Detalles del Diagnóstico</p>
                  <Button
                    onClick={(e) => { e.stopPropagation(); handleGenerateAI(); }}
                    disabled={generatingAI}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold py-2 px-3.5 flex items-center gap-1.5 shadow-sm"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${generatingAI ? 'animate-spin' : ''}`} />
                    <span>{generatingAI ? 'Analizando...' : 'Regenerar con IA'}</span>
                  </Button>
                </div>

                {latestInsight ? (
                  <div className="space-y-3">
                    {latestInsight.summary && (
                      <p className="text-sm text-slate-700 leading-relaxed font-medium bg-indigo-50/40 p-3.5 rounded-2xl border border-indigo-100/60">
                        💡 {latestInsight.summary}
                      </p>
                    )}

                    {latestInsight.recommendations && latestInsight.recommendations.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Recomendaciones Accionables</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {latestInsight.recommendations.map((rec, idx) => (
                            <div key={idx} className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex items-start gap-2 text-xs text-slate-700 font-medium">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                              <span>{typeof rec === 'string' ? rec : (rec.text || rec.title || 'Recomendación')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 space-y-2 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                    <Sparkles className="w-8 h-8 text-indigo-400 mx-auto" />
                    <p className="text-xs font-bold text-slate-600">Aún no has generado un análisis con IA</p>
                    <p className="text-[11px] text-slate-400 max-w-sm mx-auto">
                      Presiona regenerar para analizar tus patrones financieros.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 2. GRID PRINCIPAL: DETECTOR DE GASTOS HORMIGA (ARRIBA) & LIBERTAD FINANCIERA (RUNWAY) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card 1: Detector de Gastos Hormiga */}
          <Card className="p-5 sm:p-6 bg-white border-slate-100 shadow-sm rounded-3xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                  <Flame className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-slate-900 text-sm">Detector de Gastos Hormiga</h3>
                  <p className="text-[10px] text-slate-400 font-medium">Compras pequeñas (≤ $150 MXN)</p>
                </div>
              </div>
              <span className="text-[10px] font-black px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                {microExpensesData.count} compras
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-[10px] text-slate-400 font-bold uppercase">Total Acumulado</p>
                <p className="text-base font-black text-rose-600 mt-0.5">{formatCurrency(microExpensesData.totalMicro)}</p>
                <span className="text-[9px] text-slate-400">{microExpensesData.percentageOfTotal}% de tus gastos</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-[10px] text-slate-400 font-bold uppercase">Impacto Anual Proyectado</p>
                <p className="text-base font-black text-slate-900 mt-0.5">{formatCurrency(microExpensesData.projectedAnnual)}</p>
                <span className="text-[9px] text-slate-400">Si mantienes el ritmo</span>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 leading-relaxed font-medium">
              🔍 Pequeños pagos como cafés, compras rápidas o antojos pueden sumar miles de pesos al año sin notarlo.
            </p>
          </Card>

          {/* Card 2: Runway / Libertad Financiera */}
          <Card className="p-5 sm:p-6 bg-white border-slate-100 shadow-sm rounded-3xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-slate-900 text-sm">Libertad Financiera (Runway)</h3>
                  <p className="text-[10px] text-slate-400 font-medium">Meses de supervivencia sin ingresos</p>
                </div>
              </div>
              <span className={`text-[10px] font-black px-2.5 py-1 rounded-full border ${runwayData.statusColor}`}>
                {runwayData.monthsRunway} meses
              </span>
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500 font-medium">Activos disponibles:</span>
                <span className="font-bold text-slate-900">{formatCurrency(runwayData.totalAssets)}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500 font-medium">Gasto mensual promedio:</span>
                <span className="font-bold text-slate-900">{formatCurrency(runwayData.avgMonthlyExpense)}</span>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 leading-relaxed font-medium">
              🛡️ {runwayData.statusLabel}. Lo recomendado por expertos para imprevistos es mantener un colchón de entre 3 y 6 meses.
            </p>
          </Card>
        </div>

        {/* 3. TASA DE RETENCIÓN DE CAPITAL (% DE AHORRO REAL) */}
        <Card className="p-5 sm:p-6 bg-white border-slate-100 shadow-sm rounded-3xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <PiggyBank className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-black text-slate-900 text-sm">Tasa de Retención de Capital</h3>
                <p className="text-[10px] text-slate-400 font-medium">Porcentaje de ingresos que conservaste</p>
              </div>
            </div>
            <span className={`text-sm font-black px-3 py-1 rounded-full ${
              retentionData.retentionRate >= 20 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                : (retentionData.retentionRate >= 0 ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-rose-50 text-rose-700 border border-rose-200')
            }`}>
              {retentionData.retentionRate}%
            </span>
          </div>

          <div className="space-y-2">
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
              <div 
                className={`h-full transition-all duration-500 ${
                  retentionData.retentionRate >= 20 ? 'bg-emerald-500' : (retentionData.retentionRate >= 0 ? 'bg-blue-600' : 'bg-rose-500')
                }`}
                style={{ width: `${Math.max(5, Math.min(100, retentionData.retentionRate))}%` }}
              />
            </div>
            <div className="flex justify-between items-center text-[11px] text-slate-400 font-semibold">
              <span>0% (Todo gastado)</span>
              <span className="text-slate-600">Meta recomendada: 20%</span>
              <span>100% (Todo retenido)</span>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AdvancedAnalytics;
